import React from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Copy, Send, Webhook, Key, BookOpen, FlaskConical, Radio, Activity } from "lucide-react";
import { businessApi, API } from "../../lib/api";

const EVENTS = [
  "shipment.created", "shipment.ready_for_pickup", "shipment.picked_up",
  "shipment.at_hub", "shipment.sorted", "shipment.out_for_delivery",
  "shipment.delivered", "shipment.failed_delivery", "shipment.returned", "shipment.cancelled",
];

export default function BusinessApiPage() {
  const [tab, setTab] = React.useState("keys");
  const tabs = [
    { k: "keys", label: "API-nøgler", icon: Key, testid: "biz-api-tab-keys" },
    { k: "webhooks", label: "Webhooks", icon: Webhook, testid: "biz-api-tab-webhooks" },
    { k: "logs", label: "Logs", icon: Activity, testid: "biz-api-tab-logs" },
    { k: "deliveries", label: "Webhook deliveries", icon: Radio, testid: "biz-api-tab-deliveries" },
    { k: "docs", label: "API-dokumentation", icon: BookOpen, testid: "biz-api-tab-docs" },
  ];

  return (
    <div className="space-y-5" data-testid="business-api-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-brand-red font-semibold">API & Webhooks</div>
        <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">API-integration</h1>
        <p className="text-sm text-brand-text2 mt-1">Integrér dit ERP/WMS direkte med EMA Kurer. Sandbox-mode er fuldt isoleret fra live-data.</p>
      </div>

      <div className="flex border-b border-brand overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={t.testid}
                  className={`px-4 py-2 text-sm flex items-center gap-1 border-b-2 -mb-[2px] ${tab === t.k ? "border-brand-red text-brand-dark font-semibold" : "border-transparent text-brand-text2 hover:text-brand-dark"}`}>
            <t.icon size={14}/> {t.label}
          </button>
        ))}
      </div>

      {tab === "keys" && <KeysPanel/>}
      {tab === "webhooks" && <WebhooksPanel/>}
      {tab === "logs" && <LogsPanel/>}
      {tab === "deliveries" && <DeliveriesPanel/>}
      {tab === "docs" && <DocsPanel/>}
    </div>
  );
}

/* ---------- Keys ---------- */
function KeysPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [newKey, setNewKey] = React.useState(null);
  const [form, setForm] = React.useState({ label: "", is_sandbox: false });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await businessApi().get("/business/api/keys");
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await businessApi().post("/business/api/keys", form);
      setNewKey(data);
      setAdding(false);
      setForm({ label: "", is_sandbox: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke denne nøgle? Den kan ikke gendannes.")) return;
    try { await businessApi().delete(`/business/api/keys/${id}`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  return (
    <div className="space-y-4">
      {newKey && (
        <div className="bg-emerald-50 border-2 border-emerald-300 p-4 space-y-2" data-testid="biz-api-new-key-banner">
          <div className="font-semibold text-emerald-900">🔑 Ny API-nøgle oprettet — kopiér nu (vises kun denne ene gang):</div>
          <div className="flex items-center gap-2 bg-white border border-emerald-300 p-2">
            <code className="flex-1 text-xs break-all font-mono">{newKey.key}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey.key); toast.success("Kopieret"); }} className="text-xs underline text-emerald-700 inline-flex items-center gap-1" data-testid="biz-api-new-key-copy"><Copy size={12}/>Kopiér</button>
          </div>
          <button onClick={() => setNewKey(null)} className="text-xs text-emerald-700 underline">Jeg har gemt nøglen — luk denne besked</button>
        </div>
      )}

      <div className="flex justify-between">
        <p className="text-sm text-brand-text2">Brug Bearer-auth: <code className="text-xs bg-brand-bg2 px-1">Authorization: Bearer ema_live_...</code></p>
        <button onClick={() => setAdding(true)} className="btn-primary" data-testid="biz-api-new-key-btn"><Plus size={14}/> Ny API-nøgle</button>
      </div>

      {adding && (
        <form onSubmit={create} className="bg-white border border-brand p-4 grid grid-cols-2 gap-3 text-sm">
          <div><label className="label-flat">Label *</label><input className="input-flat" required value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} placeholder="fx Matas ERP integration" data-testid="biz-api-key-label"/></div>
          <div><label className="label-flat">Mode</label>
            <select className="input-flat" value={form.is_sandbox ? "sandbox" : "live"} onChange={(e) => setForm({...form, is_sandbox: e.target.value === "sandbox"})} data-testid="biz-api-key-mode">
              <option value="live">Live (production)</option>
              <option value="sandbox">Sandbox (test mode — isoleret)</option>
            </select>
          </div>
          <div className="col-span-2 flex gap-2"><button type="button" onClick={() => setAdding(false)} className="btn-secondary">Annullér</button><button type="submit" className="btn-primary ml-auto" data-testid="biz-api-key-submit">Opret</button></div>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin" size={14}/> : (
        <div className="bg-white border border-brand overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr><th className="px-3 py-2 text-left">Label</th><th className="px-3 py-2 text-left">Prefix</th><th className="px-3 py-2 text-left">Mode</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Sidst brugt</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((k) => (
                <tr key={k.id} className="border-t border-gray-100" data-testid={`biz-api-key-row-${k.id}`}>
                  <td className="px-3 py-2">{k.label}</td>
                  <td className="px-3 py-2 font-mono text-xs">{k.prefix}</td>
                  <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 ${k.is_sandbox ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{k.is_sandbox ? "Sandbox" : "Live"}</span></td>
                  <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 ${k.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-600"}`}>{k.status}</span></td>
                  <td className="px-3 py-2 text-xs text-brand-text2">{k.last_used_at ? new Date(k.last_used_at).toLocaleString("da-DK") : "Aldrig"}</td>
                  <td className="px-3 py-2 text-right">{k.status === "active" && <button onClick={() => revoke(k.id)} className="text-red-600 hover:text-red-800" title="Revoke" data-testid={`biz-api-key-revoke-${k.id}`}><Trash2 size={14}/></button>}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-brand-text2">Ingen API-nøgler endnu</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- Webhooks ---------- */
function WebhooksPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [newWh, setNewWh] = React.useState(null);
  const [form, setForm] = React.useState({ url: "", events: ["shipment.created", "shipment.delivered"], active: true, is_sandbox: false });

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const { data } = await businessApi().get("/business/api/webhooks"); setItems(data.items || []); }
    catch { toast.error("Fejl"); }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await businessApi().post("/business/api/webhooks", form);
      setNewWh(data);
      setAdding(false);
      setForm({ url: "", events: ["shipment.created", "shipment.delivered"], active: true, is_sandbox: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  const del = async (id) => {
    if (!window.confirm("Slet denne webhook?")) return;
    try { await businessApi().delete(`/business/api/webhooks/${id}`); toast.success("Slettet"); load(); }
    catch { toast.error("Fejl"); }
  };

  const testFire = async (id) => {
    try { await businessApi().post(`/business/api/webhooks/${id}/test`); toast.success("Test-event sendt"); }
    catch { toast.error("Fejl"); }
  };

  const toggleEvent = (e) => {
    setForm((p) => ({ ...p, events: p.events.includes(e) ? p.events.filter((x) => x !== e) : [...p.events, e] }));
  };

  return (
    <div className="space-y-4">
      {newWh && (
        <div className="bg-emerald-50 border-2 border-emerald-300 p-4 space-y-2" data-testid="biz-webhook-secret-banner">
          <div className="font-semibold text-emerald-900">🔐 Webhook secret — gem nu (kun denne gang):</div>
          <div className="flex items-center gap-2 bg-white border border-emerald-300 p-2">
            <code className="flex-1 text-xs break-all font-mono">{newWh.secret}</code>
            <button onClick={() => { navigator.clipboard.writeText(newWh.secret); toast.success("Kopieret"); }} className="text-xs underline text-emerald-700"><Copy size={12} className="inline"/> Kopiér</button>
          </div>
          <p className="text-xs text-emerald-800">Brug denne secret til at validere <code>X-EMA-Signature</code> headeren på dine endpoints.</p>
          <button onClick={() => setNewWh(null)} className="text-xs text-emerald-700 underline">Jeg har gemt secret&apos;en — luk</button>
        </div>
      )}

      <div className="flex justify-between">
        <p className="text-sm text-brand-text2">HMAC-SHA256 signering · Retry: 1m, 5m, 30m, 2h, 6h, 24h</p>
        <button onClick={() => setAdding(true)} className="btn-primary" data-testid="biz-webhook-new-btn"><Plus size={14}/> Ny webhook</button>
      </div>

      {adding && (
        <form onSubmit={create} className="bg-white border border-brand p-4 space-y-3 text-sm">
          <div><label className="label-flat">URL *</label><input className="input-flat" required type="url" placeholder="https://yoursystem.dk/webhooks/ema" value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} data-testid="biz-webhook-url"/></div>
          <div>
            <label className="label-flat">Events</label>
            <div className="flex flex-wrap gap-1">
              {EVENTS.map((e) => (
                <label key={e} className={`text-xs px-2 py-1 border cursor-pointer ${form.events.includes(e) ? "bg-brand-red text-white border-brand-red" : "bg-white text-brand-text2 border-gray-200"}`}>
                  <input type="checkbox" className="hidden" checked={form.events.includes(e)} onChange={() => toggleEvent(e)} data-testid={`biz-webhook-event-${e}`}/>
                  {e}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <label><input type="checkbox" checked={form.is_sandbox} onChange={(e) => setForm({...form, is_sandbox: e.target.checked})} data-testid="biz-webhook-sandbox"/> Sandbox-only</label>
            <label><input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} data-testid="biz-webhook-active"/> Aktiv</label>
          </div>
          <div className="flex gap-2"><button type="button" onClick={() => setAdding(false)} className="btn-secondary">Annullér</button><button type="submit" className="btn-primary ml-auto" data-testid="biz-webhook-submit">Opret</button></div>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin" size={14}/> : (
        <div className="space-y-2">
          {items.map((w) => (
            <div key={w.id} className="bg-white border border-brand p-3 text-sm" data-testid={`biz-webhook-row-${w.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <code className="font-mono text-xs break-all flex-1">{w.url}</code>
                <span className={`text-xs px-1.5 py-0.5 ${w.is_sandbox ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{w.is_sandbox ? "Sandbox" : "Live"}</span>
                <span className={`text-xs px-1.5 py-0.5 ${w.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-600"}`}>{w.active ? "Aktiv" : "Inaktiv"}</span>
                <button onClick={() => testFire(w.id)} className="text-xs text-blue-700 underline inline-flex items-center gap-1" data-testid={`biz-webhook-test-${w.id}`}><Send size={12}/>Test</button>
                <button onClick={() => del(w.id)} className="text-red-600" data-testid={`biz-webhook-delete-${w.id}`}><Trash2 size={14}/></button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {w.events?.map((e) => <span key={e} className="text-[10px] bg-brand-bg2 px-1.5 py-0.5">{e}</span>)}
              </div>
              <div className="text-xs text-brand-text2 mt-1">
                Failures: {w.failure_count || 0} · Sidst OK: {w.last_success_at_iso ? new Date(w.last_success_at_iso).toLocaleString("da-DK") : "—"}
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-center py-6 text-brand-text2">Ingen webhooks endnu</div>}
        </div>
      )}
    </div>
  );
}

/* ---------- Logs ---------- */
function LogsPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    (async () => {
      try { const { data } = await businessApi().get("/business/api/logs?limit=100"); setItems(data.items || []); }
      catch { toast.error("Fejl"); }
      setLoading(false);
    })();
  }, []);
  return (
    <div className="bg-white border border-brand overflow-x-auto" data-testid="biz-api-logs-panel">
      {loading ? <Loader2 className="animate-spin m-3" size={14}/> : (
        <table className="w-full text-sm">
          <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
            <tr><th className="px-3 py-2 text-left">Tidspunkt</th><th className="px-3 py-2 text-left">Method</th><th className="px-3 py-2 text-left">Endpoint</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Mode</th><th className="px-3 py-2 text-left">Fejl</th></tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className="border-t border-gray-100">
                <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(l.created_at_iso).toLocaleString("da-DK")}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.method}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.endpoint}</td>
                <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 ${l.status_code >= 400 ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>{l.status_code}</span></td>
                <td className="px-3 py-2"><span className={`text-xs ${l.is_sandbox ? "text-amber-700" : "text-emerald-700"}`}>{l.is_sandbox ? "sandbox" : "live"}</span></td>
                <td className="px-3 py-2 text-xs text-red-700">{l.error || ""}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-brand-text2">Ingen API-kald endnu</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ---------- Deliveries ---------- */
function DeliveriesPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const load = React.useCallback(async () => {
    setLoading(true);
    try { const { data } = await businessApi().get("/business/api/deliveries?limit=100"); setItems(data.items || []); }
    catch { toast.error("Fejl"); }
    setLoading(false);
  }, []);
  React.useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  return (
    <div className="space-y-2" data-testid="biz-api-deliveries-panel">
      {loading && <Loader2 className="animate-spin" size={14}/>}
      {items.map((d) => (
        <div key={d.id} className="bg-white border border-brand p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{d.event}</span>
            <code className="text-xs text-brand-text2 break-all flex-1">{d.url}</code>
            <span className={`text-xs px-1.5 py-0.5 ${d.delivered ? "bg-emerald-100 text-emerald-800" : d.attempts >= 6 ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
              {d.delivered ? "Leveret" : `Forsøg ${d.attempts}/6`}
            </span>
          </div>
          <div className="text-xs text-brand-text2 mt-1">
            {new Date(d.created_at_iso).toLocaleString("da-DK")} · HTTP {d.last_response_status || "—"}{d.last_response_body && ` · "${(d.last_response_body || "").slice(0, 60)}"`}
          </div>
        </div>
      ))}
      {!loading && items.length === 0 && <div className="text-center py-6 text-brand-text2">Ingen leveringer endnu</div>}
    </div>
  );
}

/* ---------- Docs ---------- */
function DocsPanel() {
  return (
    <div className="space-y-4">
      <TryItPanel/>
      <DocsContent/>
    </div>
  );
}

function TryItPanel() {
  const [sandboxKey, setSandboxKey] = React.useState("");
  const [endpoint, setEndpoint] = React.useState("POST /shipments");
  const [busy, setBusy] = React.useState(false);
  const [response, setResponse] = React.useState(null);
  const [sampleShipmentId, setSampleShipmentId] = React.useState("");
  const [body, setBody] = React.useState("");
  const [picks, setPicks] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      // Try to fetch a sandbox key from server (won't expose existing keys, but admin can paste manually)
      try {
        const { data } = await businessApi().get("/business/pickup-locations");
        setPicks(data.items || []);
        if (data.items?.length) {
          setBody(JSON.stringify({
            customer_reference: `TEST-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
            pickup_location_id: data.items[0].id,
            product: "next_day_b2b",
            recipient_name: "Test Recipient",
            recipient_phone: "+4520202020",
            delivery_address: "Hovedgaden 1",
            delivery_postal_code: "4140",
            delivery_city: "Borup",
            length_cm: 30, width_cm: 20, height_cm: 15, weight_kg: 2.0, colli_count: 1,
          }, null, 2));
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const run = async () => {
    if (!sandboxKey.startsWith("ema_test_")) { toast.error("Indsæt en sandbox-nøgle (starter med ema_test_)"); return; }
    setBusy(true); setResponse(null);
    try {
      const headers = { Authorization: `Bearer ${sandboxKey}`, "Content-Type": "application/json" };
      let url = ""; let method = "GET"; let payload = null;
      if (endpoint === "POST /shipments") { url = `${API}/v1/b2b/shipments`; method = "POST"; payload = JSON.parse(body); }
      else if (endpoint === "POST /shipments/bulk") { url = `${API}/v1/b2b/shipments/bulk`; method = "POST"; payload = { shipments: [JSON.parse(body)] }; }
      else if (endpoint === "GET /shipments/{id}") { url = `${API}/v1/b2b/shipments/${sampleShipmentId}`; method = "GET"; }
      else if (endpoint === "GET /shipments/{id}/tracking") { url = `${API}/v1/b2b/shipments/${sampleShipmentId}/tracking`; method = "GET"; }
      else if (endpoint === "GET /me") { url = `${API}/v1/b2b/me`; method = "GET"; }

      const res = await fetch(url, { method, headers, body: payload ? JSON.stringify(payload) : undefined });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = text; }
      setResponse({ status: res.status, ok: res.ok, body: parsed });
      // auto-fill sampleShipmentId from POST response
      if (res.ok && parsed?.id) setSampleShipmentId(parsed.id);
    } catch (e) { setResponse({ status: 0, ok: false, body: String(e) }); }
    finally { setBusy(false); }
  };

  const needsBody = endpoint.startsWith("POST");
  const needsId = endpoint.includes("{id}");

  return (
    <div className="bg-amber-50 border-2 border-amber-300 p-5 space-y-3" data-testid="biz-api-tryit-panel">
      <div className="flex items-center gap-2">
        <FlaskConical size={18} className="text-amber-700"/>
        <h3 className="font-display font-bold text-lg text-amber-900">Try it — sandbox</h3>
        <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5">ISOLERET</span>
      </div>
      <p className="text-sm text-amber-800">Test API&apos;en direkte her med din sandbox-nøgle. Forsendelser oprettet her gemmes i den separate sandbox-collection og påvirker IKKE live-data.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div>
          <label className="label-flat">Sandbox API-nøgle (ema_test_*)</label>
          <input type="password" className="input-flat font-mono" placeholder="ema_test_..." value={sandboxKey} onChange={(e) => setSandboxKey(e.target.value)} data-testid="biz-api-tryit-key"/>
          <p className="text-xs text-amber-800 mt-1">Opret en sandbox-nøgle på API-nøgler tabben.</p>
        </div>
        <div>
          <label className="label-flat">Endpoint</label>
          <select className="input-flat" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} data-testid="biz-api-tryit-endpoint">
            <option>GET /me</option>
            <option>POST /shipments</option>
            <option>POST /shipments/bulk</option>
            <option value="GET /shipments/{id}">{"GET /shipments/{id}"}</option>
            <option value="GET /shipments/{id}/tracking">{"GET /shipments/{id}/tracking"}</option>
          </select>
        </div>
      </div>

      {needsBody && (
        <div>
          <label className="label-flat">Body (JSON)</label>
          <textarea rows={9} className="input-flat font-mono text-xs" value={body} onChange={(e) => setBody(e.target.value)} data-testid="biz-api-tryit-body"/>
          {picks.length === 0 && <p className="text-xs text-red-700">Opret en pickup-lokation først for at få et gyldigt pickup_location_id.</p>}
        </div>
      )}

      {needsId && (
        <div>
          <label className="label-flat">Shipment ID</label>
          <input className="input-flat font-mono" value={sampleShipmentId} onChange={(e) => setSampleShipmentId(e.target.value)} placeholder="uuid-..." data-testid="biz-api-tryit-id"/>
        </div>
      )}

      <button onClick={run} disabled={busy || !sandboxKey} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 inline-flex items-center gap-2" data-testid="biz-api-tryit-run">
        {busy ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>} Kør test-request
      </button>

      {response && (
        <div className="bg-white border border-amber-300 p-3" data-testid="biz-api-tryit-response">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs px-2 py-0.5 ${response.ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>HTTP {response.status}</span>
            <span className="text-xs text-brand-text2">Response:</span>
          </div>
          <pre className="text-xs overflow-x-auto bg-brand-bg2 p-2 max-h-72">{typeof response.body === "string" ? response.body : JSON.stringify(response.body, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function DocsContent() {
  return (
    <div className="bg-white border border-brand p-6 space-y-6 text-sm" data-testid="biz-api-docs-panel">
      <section>
        <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Authentication</h3>
        <p className="text-brand-text2">Brug Bearer-token authentication med din API-nøgle. Live keys starter med <code>ema_live_</code>, sandbox keys starter med <code>ema_test_</code>.</p>
        <pre className="bg-brand-bg2 p-3 text-xs mt-2 overflow-x-auto"><code>{`curl -H "Authorization: Bearer ema_live_xxxxxxxxxxx" \\
     https://emakurer.dk/api/v1/b2b/me`}</code></pre>
      </section>

      <section>
        <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Sandbox-mode</h3>
        <p className="text-brand-text2">Sandbox-nøgler (<code>ema_test_*</code>) bruger separate sandbox-collections — ingen risiko for at påvirke live-data, fakturaer eller chauffør-flow. Sandbox shipments får prefix <code>EMATST-YYYY-NNNNNN</code>.</p>
      </section>

      <section>
        <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Endpoints</h3>
        <ul className="space-y-3">
          <Endpoint method="GET" path="/api/v1/b2b/me" desc="Verificér nøgle + kunde-info"/>
          <Endpoint method="POST" path="/api/v1/b2b/shipments" desc="Opret en forsendelse. Brug Idempotency-Key header eller customer_reference for at undgå dubletter."/>
          <Endpoint method="POST" path="/api/v1/b2b/shipments/bulk" desc="Opret op til 500 forsendelser i ét kald. Returnerer per-row resultat."/>
          <Endpoint method="GET" path="/api/v1/b2b/shipments" desc="List forsendelser. Filter: ?status, ?product, ?limit"/>
          <Endpoint method="GET" path="/api/v1/b2b/shipments/{id}" desc="Hent enkelt forsendelse"/>
          <Endpoint method="GET" path="/api/v1/b2b/shipments/{id}/tracking" desc="Hent tracking events"/>
          <Endpoint method="DELETE" path="/api/v1/b2b/shipments/{id}" desc="Annullér forsendelse (kun før pickup)"/>
          <Endpoint method="GET" path="/api/v1/b2b/pickup-locations" desc="Liste over dine pickup-lokationer"/>
        </ul>
      </section>

      <section>
        <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Eksempel: Opret forsendelse</h3>
        <pre className="bg-brand-bg2 p-3 text-xs overflow-x-auto"><code>{`POST /api/v1/b2b/shipments
Authorization: Bearer ema_live_xxxxx
Idempotency-Key: ORDER-12345
Content-Type: application/json

{
  "customer_reference": "ORDER-12345",
  "pickup_location_id": "uuid-...",
  "product": "next_day_b2b",
  "recipient_name": "Anders Andersen",
  "recipient_phone": "+4520202020",
  "recipient_email": "anders@example.dk",
  "delivery_address": "Hovedgaden 12",
  "delivery_postal_code": "4140",
  "delivery_city": "Borup",
  "delivery_notes": "Aflever ved bagdør",
  "length_cm": 30, "width_cm": 20, "height_cm": 15,
  "weight_kg": 2.5, "colli_count": 1
}`}</code></pre>
      </section>

      <section>
        <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Webhook signering (HMAC-SHA256)</h3>
        <p className="text-brand-text2 mb-2">Hver webhook indeholder en signatur-header. Verificér den ved at beregne HMAC over <code>{`<timestamp>.<body>`}</code>:</p>
        <pre className="bg-brand-bg2 p-3 text-xs overflow-x-auto"><code>{`X-EMA-Signature: t=1727431200,v1=abc123...
X-EMA-Event: shipment.delivered
X-EMA-Delivery-Id: uuid-...

// Python verificering:
import hmac, hashlib
ts, sig = header.split(",")
ts = ts.split("=")[1]
sig = sig.split("=")[1]
expected = hmac.new(SECRET.encode(), f"{ts}.".encode() + body, hashlib.sha256).hexdigest()
assert hmac.compare_digest(expected, sig)`}</code></pre>
      </section>

      <section>
        <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Webhook events</h3>
        <ul className="grid grid-cols-2 gap-1 text-xs">
          {EVENTS.map((e) => <li key={e} className="bg-brand-bg2 px-2 py-1 font-mono">{e}</li>)}
        </ul>
      </section>

      <section>
        <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Fejlkoder</h3>
        <table className="w-full text-xs">
          <thead className="bg-brand-bg2"><tr><th className="px-2 py-1 text-left">Kode</th><th className="px-2 py-1 text-left">Betydning</th></tr></thead>
          <tbody>
            <tr className="border-t"><td className="px-2 py-1">400</td><td className="px-2 py-1">Validation error (vægt &gt; 11 kg, postnr uden for Sjælland, ugyldigt produkt, cutoff overskredet)</td></tr>
            <tr className="border-t"><td className="px-2 py-1">401</td><td className="px-2 py-1">Manglende / ugyldig / revoked API-nøgle</td></tr>
            <tr className="border-t"><td className="px-2 py-1">404</td><td className="px-2 py-1">Forsendelse eller pickup-lokation ikke fundet</td></tr>
            <tr className="border-t"><td className="px-2 py-1">429</td><td className="px-2 py-1">Rate limit overskredet (100 req/min/nøgle)</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h3 className="font-display font-bold text-xl text-brand-dark mb-2 flex items-center gap-2"><FlaskConical size={18}/>Test-data oprydning</h3>
        <p className="text-brand-text2">Sandbox-data slettes automatisk efter 14 dage (kører hver nat kl. 03:00 KBH). Webhook deliveries gemmes i 30 dage. API-logs gemmes i 90 dage.</p>
      </section>
    </div>
  );
}

function Endpoint({ method, path, desc }) {
  const colors = { GET: "bg-blue-100 text-blue-800", POST: "bg-emerald-100 text-emerald-800", DELETE: "bg-red-100 text-red-800" };
  return (
    <li className="flex items-start gap-3">
      <span className={`text-xs font-mono px-2 py-0.5 ${colors[method]}`}>{method}</span>
      <div className="flex-1 min-w-0">
        <code className="text-xs font-mono break-all">{path}</code>
        <div className="text-xs text-brand-text2">{desc}</div>
      </div>
    </li>
  );
}
